import { command, query, getRequestEvent } from '$app/server';
import { error } from '@sveltejs/kit';
import { supabaseAdmin } from '#lib/server/supabaseAdmin.js';
import { getUserPremiumStatus } from '#lib/server/premium.js';
import type { Board, Match, Tile, Turn } from '~/types';

export type ClassicSnapshotMeta = {
  id: string;
  score: number | null;
  swaps: number | null;
  latest_word: Match | null;
  edited_at: string;
  completed: boolean;
  words: number;
  turns: number;
};

export type SnapshotPayload = {
  id: string;
  board: Board<Tile>;
  score: number;
  swaps: number;
  shuffles: number;
  streak: number;
  streakLevel: number;
  streakInterval: number;
  startedAt?: number;
  latestWord?: Match;
  lost?: boolean;
  editedAt: string;
  lastSyncedAt?: string;
  overwrite?: boolean;
  wordPointer: number;
  turnPointer: number;
  newWords: Match[];
  newTurns: Turn[];
};

export type StoredSnapshot = Omit<SnapshotPayload, 'newWords' | 'newTurns' | 'wordPointer' | 'turnPointer'> & {
  editedAt: string;
  words: Match[];
  turns: Turn[];
};

export const getClassicSnapshot = query(async (): Promise<ClassicSnapshotMeta> => {
  const { locals } = getRequestEvent();
  if (!locals.user) error(401, 'not authorized');

  const { data, error: dbError } = await locals.supabase
    .from('classic_snapshots')
    .select('*')
    .eq('lost', false)
    .order('edited_at', { ascending: false })
    .limit(1)
    .single();

  if (dbError) {
    error(400, dbError.hint);
  }

  return data;
});

export type UpsertResult = { ok: true, editedAt: string } | { ok: false; reason: 'pointer_mismatch' | 'sync_conflict' };

export const upsertClassicSnapshot = command(
  'unchecked',
  async (payload: SnapshotPayload): Promise<UpsertResult> => {
    const { locals } = getRequestEvent();
    if (!locals.user) error(401, 'not authorized');

    const premium = await getUserPremiumStatus(locals.supabase);
    if (!premium) error(403, 'premium required');

    const { data: latest } = await locals.supabase
      .from('classic_snapshots')
      .select('*')
      .eq('user_id', locals.user.id)
      .neq('lost', true)
      .order('edited_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (latest && payload.lastSyncedAt && !payload.overwrite) {
      // games are identical, no need to sync
      if (latest.id === payload.id && latest.score === payload.score && latest.turns === payload.turnPointer) {
        return { ok: true, editedAt: latest.edited_at };
      }
      // if snapshot is newer than payload
      else if (new Date(latest.edited_at) > new Date(payload.lastSyncedAt)) {
        return { ok: false, reason: 'sync_conflict' };
      }
    }

    const { id, wordPointer, turnPointer, newWords, newTurns, ...meta } = payload;

    const { data: existingFile } = await supabaseAdmin.storage
      .from('classic-games')
      .download(`${id}.json`);

    let storedWords: Match[] = [];
    let storedTurns: Turn[] = [];

    if (existingFile) {
      const prev: StoredSnapshot = JSON.parse(await existingFile.text());
      storedWords = prev.words ?? [];
      storedTurns = prev.turns ?? [];
    }

    const sameDeviceAndGame = latest?.id === id && latest?.device_id === locals.userId;
    if (!payload.overwrite && !sameDeviceAndGame && (storedWords.length !== wordPointer || storedTurns.length !== turnPointer)) {
      return { ok: false, reason: 'pointer_mismatch' };
    }

    const baseWords = payload.overwrite ? storedWords.slice(0, wordPointer) : storedWords;
    const baseTurns = payload.overwrite ? storedTurns.slice(0, turnPointer) : storedTurns;

    const allWords = [...baseWords, ...newWords];
    const allTurns = [...baseTurns, ...newTurns];

    const lost = meta.lost || meta.swaps <= 0;

    const snapshot: StoredSnapshot = {
      ...meta,
      lost,
      id,
      editedAt: new Date().toISOString(),
      words: allWords,
      turns: allTurns,
    };

    const { error: uploadError } = await supabaseAdmin.storage
      .from('classic-games')
      .upload(`${id}.json`, JSON.stringify(snapshot), {
        upsert: true,
        contentType: 'application/json',
      });

    if (uploadError) error(500, uploadError.message);

    const { error: dbError } = await locals.supabase
      .from('classic_snapshots')
      .upsert({
        id,
        user_id: locals.user.id,
        device_id: locals.userId,
        score: meta.score,
        swaps: meta.swaps,
        latest_word: meta.latestWord,
        edited_at: meta.editedAt,
        lost,
        words: allWords.length,
        turns: allTurns.length,
      });

    if (dbError) error(500, dbError.message);

    return { ok: true, editedAt: snapshot.editedAt };
  },
);

export const getClassicSnapshotById = query(
  'unchecked',
  async (id: string): Promise<StoredSnapshot> => {
    const { locals } = getRequestEvent();
    if (!locals.user) error(401, 'not authorized');

    const premium = await getUserPremiumStatus(locals.supabase);
    if (!premium) error(403, 'premium required');

    const { data, error: dlError } = await supabaseAdmin.storage
      .from('classic-games')
      .download(`${id}.json`);

    if (dlError || !data) error(404, 'snapshot not found');

    return JSON.parse(await data.text());
  },
);

export const deleteClassicSnapshotById = command(
  'unchecked',
  async (id: string) => {
    const { locals } = getRequestEvent();
    if (!locals.user) error(401, 'not authorized');

    const premium = await getUserPremiumStatus(locals.supabase);
    if (!premium) error(403, 'premium required');

    const { error: dbError } = await locals.supabase
      .from('classic_snapshots')
      .delete()
      .eq('id', id)
      .eq('user_id', locals.user.id);

    if (dbError) error(500, 'failed to delete snapshot record');

    return await locals.supabase.storage
      .from('classic-games')
      .remove([`${id}.json`]);
  },
);
